import Backbone from 'backbone'
import template from 'scoreboard/admin/connections/rest/rest-connection-metadata.hbs'
import i18n from 'scoreboard/components/i18n'
import RestConnectionWizardView, {
	TOKEN_OPTIONS
} from 'scoreboard/admin/connections/rest/rest-connection-wizard-view'
import enums from 'scoreboard/components/enums'
import dropdown from 'scoreboard/components/dropdown'
import formHelper from 'scoreboard/components/form-helper'
import FormView from 'scoreboard/components/form-view'
import _ from 'lodash'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import constants from 'scoreboard/components/constants'
import inputLengthValidatorFactory from 'scoreboard/components/validators/input-length-validator-factory'
import WsAuthenticationType from 'scoreboard/admin/model/ws-authentication-type'
const { OAUTH_V2, BASIC_HTTP } = WsAuthenticationType
import WsConstants from 'scoreboard/admin/model/ws-constants'
const {
	PASSWORD_MAX_LENGTH, USERNAME_MAX_LENGTH, WS_VENDOR_TYPE
} = WsConstants
import HttpRequestForm from 'scoreboard/components/http-request-form'
import OracleNetSuiteMetadata from 'scoreboard/admin/connections/rest/vendor-specific/oracle-netsuite-metadata'
import * as urlToLinks from 'scoreboard/components/urls-to-links'

const RestConnectionMetadata = Backbone.View.extend(/** @lends {RestConnectionMetadata} */{

	tagName: 'form',

	className: 'rest-properties-dialog',

	events: {
		'change [name=wsAuthenticationType]': 'toggle',
	},

	/**
	 * First step in the rest-connection-wizard, where the details of a REST
	 * connection are added or edited.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Connection} options.model
	 *
	 * @param {Wizard} options.wizard the current wizard
	 */
	initialize: function(options = {}) {
		this.options = options
		this.formView = new FormView({
			el: this.el,
			allowCleanSubmission: true,
			submit: () => this.submit(),
		})
	},

	render: function() {
		RestConnectionWizardView.prototype.render.apply(this, arguments)
		this.$('.dialog-body').replaceWith(template(this.context()))
		this.renderWsAuthenticationType()
		this.renderHttpRequestForm()
		this.handleSecrets()
		this.toggle()
		this.renderVendorSpecificControls()
		formHelper.writeToDom(this.model, this.el)
		this.markRequiredFields()
		return this
	},

	renderHttpRequestForm: function() {
		const options = RestConnectionWizardView.prototype.getHttpRequestFormOptions
			.call(this, TOKEN_OPTIONS.ACCESS_TOKEN)
		this.httpRequestForm = new HttpRequestForm(options).render()
		this.$('.http-request-form').replaceWith(this.httpRequestForm.$el)
	},

	context: function() {
		const href = 'https://support.spiderstrategies.com/hc/en-us/articles/19855820013204-Importing-Data-from-Web-Services'
		const labelCode = 'admin.import.datasource.from.rest.banner.label'
		const link = urlToLinks.createAnchor(href, labelCode)

		const additionalBannerText = i18n.get('admin.importConnections.web-service.restConnection.instructions', link)
		return {
			additionalBannerText
		}
	},

	handleSecrets: function() {
		if (this.model.isNew()) return

		if (_.isEmpty(this.$('[name=password]').val())) {
			this.$('[name=password]').val(constants.PASSWORD_PLACEHOLDER_KEY)
		}
	},

	/**
	 * Toggles the visibility of controls based on the user's current selections
	 */
	toggle: function() {
		this.formView.clearErrors()
		let fromDom = formHelper.readFromDom(this.el)

		// username/password is only visible for basic HTTP
		this.$('.username, .password').toggleClass(
			'is-not-displayed', fromDom.wsAuthenticationType !== BASIC_HTTP)

		const isOauth = fromDom.wsAuthenticationType === OAUTH_V2
		this.$('.oauth-paragraph').toggleClass('is-not-displayed', !isOauth)
		// the whole http request form is only visible for oauth
		this.$('.http-request-form').toggleClass('is-not-displayed', !isOauth)
	},

	renderVendorSpecificControls: function() {
		const attrs = this.model.attributes
		if (_.isNil(attrs.wsVendorType)) return

		if (attrs.wsVendorType === WS_VENDOR_TYPE.ORACLE_NETSUITE) {
			this.vendorSpecificMetadata = new OracleNetSuiteMetadata({
				$parent: this.$el,
				...this.options
			}).render()
		}
	},

	renderWsAuthenticationType: function() {
		let options = enums.toOptionData('wsAuthenticationType')
		dropdown.idLabels(this.$('[name=wsAuthenticationType]'), options,
			this.model.get('wsAuthenticationType') || OAUTH_V2)
	},

	markRequiredFields: function() {
		let usernameLenValidator = inputLengthValidatorFactory({ high: USERNAME_MAX_LENGTH })
		let passwordLenValidator = inputLengthValidatorFactory({ high: PASSWORD_MAX_LENGTH })
		this.requiredFields = [
			new MarkedField({
				el: this.$('[name=username]'),
				validators: [requiredValidator, usernameLenValidator]
			}).render(),
			new MarkedField({
				el: this.$('[name=password]'),
				// #53864 password is not always required
				validators: passwordLenValidator
			}).render(),
		]
	},

	saveState: function() {
		const fromDom = formHelper.readFromDom(this.el)
		const attrs = this.model.attributes
		if (!_.isNil(fromDom.wsAuthenticationType)) {
			attrs.wsAuthenticationType = fromDom.wsAuthenticationType
		}
		attrs.username = _.trim(fromDom.username)
		attrs.password = _.trim(fromDom.password)

		RestConnectionWizardView.prototype.saveHttpRequestFormState
			.call(this, TOKEN_OPTIONS.ACCESS_TOKEN)

		this.vendorSpecificMetadata?.saveState()
	},

	shouldValidate: function() {
		// if the connection hasn't been saved yet, it can't possibly have a refresh_token
		// so we will always call the /validate endpoint in this case
		if (_.isNil(this.model.id)) return true

		const attrs = this.model.attributes
		// we don't want to hit the /validate endpoint if the connection has a refresh
		// token, because if the access_token is expired, and the configuration for
		// how to use the refresh_token is invalid, then the user won't be able to
		// get to the next step of the wizard to correct the refresh_token configuration!
		return attrs.wsAuthenticationType === OAUTH_V2 && _.isNil(attrs.refreshToken)
	},

	submit: function() {
		RestConnectionWizardView.prototype.submitAccessOrRefreshTokenView.call(this, () => {
			const attrs = this.model.attributes
			if (attrs.wsAuthenticationType !== OAUTH_V2) {
				this.options.wizard.next()
				return true
			}

			return false
		})
	},

	autoRemove: true

})

export default RestConnectionMetadata

RestConnectionMetadata.__filename__ = 'admin/connections/rest/rest-connection-metadata.js'
